import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, LoginResult, SelectCustomerResult } from './AuthContext';
import { authService, isAuthRejection, type SessionCheck } from '@/services/authService';
import { createSessionRecheck, watchForRecovery } from './sessionRecovery';
import { logAuth } from '@/lib/breadcrumbs';
import { getCustomer } from '@/lib/api/customers';
import { User, Customer, RecordingStatus } from './types';
import { useToast } from '@/hooks/use-toast';

/**
 * Resolve the active customer WITHOUT fetching the full customer list.
 *
 * Order:
 *  1. A previously-stored ``active_customer`` object (localStorage) — used
 *     as-is, no network call.
 *  2. The user's ``last_active_customer`` id — fetched as a SINGLE customer
 *     via ``GET /crm/customers/{id}/`` (never the whole list).
 *
 * Returns ``null`` when neither is available — the caller leaves
 * ``activeCustomer`` unset and the ``/select-customer`` picker takes over
 * (which is the only surface allowed to fetch the full list).
 */
const resolveActiveCustomer = async (user: User | null): Promise<Customer | null> => {
  const stored = localStorage.getItem('active_customer');
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Customer;
      if (parsed?.id) return parsed;
    } catch {
      // Corrupt entry — fall through to the last-active-customer path.
    }
  }

  const lastActiveId = user?.last_active_customer;
  if (lastActiveId) {
    try {
      const customer = await getCustomer(String(lastActiveId));
      return customer ?? null;
    } catch (error) {
      console.warn('Could not fetch last-active customer; deferring to picker', error);
    }
  }

  return null;
};

/**
 * Authentication Provider Component
 * 
 * @description
 * This component provides authentication state and functionality to the entire application.
 * It handles:
 * - User authentication (login/logout)
 * - Session initialization and persistence
 * - Customer selection and management
 * - Session recording capabilities
 * 
 * The AuthProvider wraps the application and exposes authentication functionality
 * through the AuthContext, which can be accessed using the useAuth hook.
 * 
 * @example
 * ```tsx
 * // In your app's root component
 * import { AuthProvider } from '@/contexts/auth';
 * 
 * const App = () => (
 *   <AuthProvider>
 *     <YourApp />
 *   </AuthProvider>
 * );
 * ```
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Auth state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // The session check could not reach the backend; the tokens are intact.
  const [isUnreachable, setIsUnreachable] = useState(false);
  // A single re-check (Try again / background) is running.
  const [isRechecking, setIsRechecking] = useState(false);

  // User data
  const [user, setUser] = useState<User | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [activeCustomer, setActiveCustomer] = useState<Customer | null>(null);

  // Recording status
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>({
    isRecording: false,
    isPaused: false,
  });

  /**
   * Initialize authentication state on application load
   * 
   * @description
   * Checks for existing session, restores user and customer state if available,
   * and sets up the initial authentication state of the application.
   */
  /**
   * Apply the answer of a session check to the auth state. Shared by start-up
   * and by the re-checks behind the "can't reach the server" screen.
   */
  const applySession = useCallback(async (session: SessionCheck) => {
    setIsUnreachable(session.unreachable);
    if (session.unreachable) {
      // Names the failing call next time this happens (#433 never could).
      logAuth('Session check: server unreachable', { failure: session.failure });
      return;
    }
    if (!session.isValid) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    // Set authenticated state
    setIsAuthenticated(true);
    setUser(session.user);

    // Resolve the active customer WITHOUT fetching the full list.
    // ``checkSession`` already read any stored ``active_customer``;
    // otherwise resolve the user's ``last_active_customer`` via a
    // single ``getCustomer(id)``. The full ``GET /crm/customers/``
    // list is reserved for the ``/select-customer`` picker.
    if (session.activeCustomer) {
      setActiveCustomer(session.activeCustomer);
    } else {
      const resolved = await resolveActiveCustomer(session.user);
      if (resolved) {
        await authService.selectCustomer(resolved);
        setActiveCustomer(resolved);
      }
      // No resolvable customer → leave unset; AuthGuard routes the
      // user to ``/select-customer`` which fetches the list.
    }

    // Check if session recording is active (only if needed)
    const recordingState = await authService.getRecordingStatus();
    setRecordingStatus(recordingState);
    // Replay lives in memory only, so a reload must re-start it.
    if (recordingState.isRecording && !recordingState.isPaused) {
      await authService.startRecording();
    }
  }, []);

  /**
   * Initialize authentication state on application load
   * 
   * @description
   * Checks for existing session, restores user and customer state if available,
   * and sets up the initial authentication state of the application.
   */
  const initialize = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // A session that could not be VERIFIED (server unreachable, tokens
      // intact) is not a signed-out session: re-check for up to ~20 s, then
      // let the guards show "can't reach the server" instead of /login (#433).
      await applySession(await authService.checkSessionPatiently());
    } catch (error) {
      console.error("Auth initialization error:", error);
      // Only an auth rejection ends the session. A transient failure (dropped
      // request, 5xx) here used to wipe the tokens too, logging the user out
      // on a single network blip; leave them so the next load recovers.
      if (isAuthRejection(error)) {
        authService.clearSession();
      }
    } finally {
      setIsInitialized(true);
      setIsLoading(false);
    }
  }, [applySession]);

  // One bounded re-check, shared by Try again and the background triggers.
  const recheck = useMemo(() => createSessionRecheck(applySession, {
    onError: (error) => {
      console.error('Session re-check error:', error);
      if (isAuthRejection(error)) authService.clearSession();
    },
  }), [applySession]);

  /** Try again: ONE check, with the screen kept up (button spinner). */
  const retrySession = useCallback(async () => {
    setIsRechecking(true);
    try {
      await recheck();
    } finally {
      setIsRechecking(false);
    }
  }, [recheck]);

  // Behind the "can't reach the server" screen, keep trying on our own:
  // back online, tab visible again, and every ~20 s.
  useEffect(() => {
    if (!isUnreachable) return;
    return watchForRecovery(recheck);
  }, [isUnreachable, recheck]);

  /** The way out of the retry screen: drop the tokens, go to the login form. */
  const signInAgain = useCallback(() => {
    authService.clearSession();
    setIsUnreachable(false);
    setIsAuthenticated(false);
    setUser(null);
    setActiveCustomer(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  /**
   * Handles user login
   * 
   * @description
   * Authenticates a user with email and password, loads their available customers,
   * and optionally starts session recording. On success, it attempts to restore
   * the last active customer or guides the user to select one.
   * 
   * @param email - User's email address
   * @param password - User's password
   * @param recordSession - Whether to start session recording after login
   * @param redirectTo - Optional override for redirect destination (defaults to dashboard)
   * @returns Promise with login result and redirect information
   */
  const login = async (
    email: string, 
    password: string, 
    recordSession = false,
    redirectTo?: string
  ): Promise<LoginResult> => {
    try {
      setIsLoading(true);
      const result = await authService.login(email, password);
      
      if (result.success) {
        // Set auth state
        setIsAuthenticated(true);
        setUser(result.user);

        // Start recording if requested
        if (recordSession) {
          await authService.startRecording();
          setRecordingStatus({ isRecording: true, isPaused: false });
          
          toast({
            title: "Session Recording Active",
            description: "Your session is being recorded for support purposes.",
            duration: 3000,
          });
        } else {
          // Unchecked (the default) means no recording, even if an earlier
          // session left it on.
          await authService.stopRecording();
          setRecordingStatus({ isRecording: false, isPaused: false });
        }
        
        // Broadcast login event to other tabs
        localStorage.setItem('login-event', Date.now().toString());
        
        // Welcome message
        toast({
          title: `Welcome ${result.user.first_name || email}!`,
          description: "You have successfully logged in.",
          duration: 3000,
        });
        
        // Resolve the active customer WITHOUT fetching the full list:
        // a fresh login has no stored ``active_customer``, so this hits
        // the ``last_active_customer`` → single ``getCustomer(id)`` path.
        // When it can't resolve (no last-active customer, or the user has
        // several to choose from) we leave it unset and the
        // ``/select-customer`` picker fetches the list and lets the user
        // pick. ``LoginForm`` always routes to ``/select-customer`` after a
        // successful login, which auto-forwards to ``redirectTo`` once an
        // active customer is set.
        const resolved = await resolveActiveCustomer(result.user);
        if (resolved) {
          await authService.selectCustomer(resolved);
          setActiveCustomer(resolved);
          console.log(`Active customer ${resolved.name} resolved and activated`);
        } else {
          console.log('No last-active customer; deferring to the customer picker');
        }

        const finalRedirectTo = redirectTo || '/dashboard';
        console.log(`Login successful, redirecting to: ${finalRedirectTo}`);
        return { success: true, redirectTo: finalRedirectTo };
      }
      
      return { 
        success: false, 
        error: result.error || "Invalid credentials. Please try again." 
      };
    } catch (error) {
      console.error("Login error:", error);
      return { 
        success: false, 
        error: "An unexpected error occurred. Please try again." 
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles user logout
   * 
   * @description
   * Logs out the current user, stops any active recording sessions,
   * clears authentication state, and redirects to the login page.
   */
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Stop recording if active
      if (recordingStatus.isRecording) {
        await authService.stopRecording();
      }
      
      // Perform logout
      await authService.logout();
      
      // Clear auth state
      setIsAuthenticated(false);
      setIsUnreachable(false);
      setUser(null);
      setCustomers([]);
      setActiveCustomer(null);
      setRecordingStatus({ isRecording: false, isPaused: false });
      
      // Redirect to login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      
      // Force logout even on error
      authService.clearSession();
      setIsAuthenticated(false);
      setIsUnreachable(false);
      setUser(null);
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles customer selection
   * 
   * @description
   * Updates the active customer for the current user session,
   * updates user preferences, and persists the selection.
   * Also clears React Query cache and resets site selection
   * to ensure proper context switching between customers.
   * 
   * @param customer - The customer to select
   * @returns Promise with selection result
   */
  const selectCustomer = async (customer: Customer): Promise<SelectCustomerResult> => {
    try {
      setIsLoading(true);
      
      // Get QueryClient instance
      const queryClient = await import('@/lib/queryClient').then(module => module.queryClient);
      
      const result = await authService.selectCustomer(customer);
      
      if (result.success) {
        // Set active customer in state
        setActiveCustomer(customer);
        
        // Clear the React Query cache to prevent stale data from previous customer
        queryClient.clear();
        
        // Update user with new last_active_customer
        if (user) {
          setUser({
            ...user,
            last_active_customer: customer.id,
          });
        }
        
        // Remove the active site from localStorage to trigger auto-selection
        localStorage.removeItem('active_site');
        
        // Invalidate site queries to force a refresh
        queryClient.invalidateQueries({ queryKey: ['sites'] });
        
        // Dispatch an event to notify components about customer change
        window.dispatchEvent(new CustomEvent('customer-changed', {
          detail: { customerId: customer.id }
        }));
        
        return { success: true };
      }
      
      return {
        success: false,
        error: result.error || "Failed to switch customer. Please try again."
      };
    } catch (error) {
      console.error("Select customer error:", error);
      return {
        success: false,
        error: "An unexpected error occurred. Please try again."
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Starts a new session recording
   * 
   * @description
   * Begins recording the user's session for support and debugging purposes.
   * Updates recording status and displays a notification.
   */
  const startRecording = useCallback(() => {
    authService.startRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: false });
      
      toast({
        title: "Session Recording Started",
        description: "Your session is now being recorded.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error starting recording:", error);
    });
  }, [toast]);

  /**
   * Pauses the current session recording
   * 
   * @description
   * Temporarily pauses an active session recording.
   * Updates recording status and displays a notification.
   */
  const pauseRecording = useCallback(() => {
    if (!recordingStatus.isRecording || recordingStatus.isPaused) return;
    
    authService.pauseRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: true });
      
      toast({
        title: "Session Recording Paused",
        description: "Your session recording has been paused.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error pausing recording:", error);
    });
  }, [recordingStatus, toast]);

  /**
   * Resumes a paused session recording
   * 
   * @description
   * Resumes a previously paused session recording.
   * Updates recording status and displays a notification.
   */
  const resumeRecording = useCallback(() => {
    if (!recordingStatus.isRecording || !recordingStatus.isPaused) return;
    
    authService.resumeRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: false });
      
      toast({
        title: "Session Recording Resumed",
        description: "Your session recording has resumed.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error resuming recording:", error);
    });
  }, [recordingStatus, toast]);

  /**
   * Stops the current session recording
   * 
   * @description
   * Ends the current session recording and finalizes the recording.
   * Updates recording status and displays a notification.
   */
  const stopRecording = useCallback(() => {
    if (!recordingStatus.isRecording) return;
    
    authService.stopRecording().then(() => {
      setRecordingStatus({ isRecording: false, isPaused: false });
      
      toast({
        title: "Session Recording Stopped",
        description: "Your session recording has been stopped and saved.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error stopping recording:", error);
    });
  }, [recordingStatus, toast]);

  useEffect(() => {
    const onForceLogout = () => {
      logout();
    };
    window.addEventListener('force-logout', onForceLogout);
    return () => window.removeEventListener('force-logout', onForceLogout);
  }, [logout]);

  // Context value
  const contextValue = {
    isAuthenticated,
    isInitialized,
    isLoading,
    isRefreshing,
    isUnreachable,
    isRechecking,
    retrySession,
    signInAgain,
    user,
    customers,
    activeCustomer,
    recordingStatus,
    login,
    logout,
    selectCustomer,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
